import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from '@tanstack/react-query';
import { MutationFetchFunction } from 'common/helpers/common';
import { ApiError, ApiResult } from 'common/repositories/common.model';

import {
  CompleteableMutationInput,
  DestroyableMutationInput,
  PrintInvoiceMutationInput,
  PrintVoucherMutationInput,
  ResolveableMutationInput,
  RevertDraftMutationInput,
  RevertVoidMutationInput,
  VoidableMutationInput,
} from './model';

export function useDestroy(
  options?: UseMutationOptions<
    ApiResult<{ message: string }>,
    ApiError,
    DestroyableMutationInput
  >,
): UseMutationResult<
  ApiResult<{ message: string }>,
  ApiError,
  DestroyableMutationInput
> {
  return useMutation<
    ApiResult<{ message: string }>,
    ApiError,
    DestroyableMutationInput
  >(async function ({ id, type }) {
    return await MutationFetchFunction({
      method: 'DELETE',
      url: `${type}/${id}`,
    });
  }, options);
}

export function useComplete(
  options?: UseMutationOptions<
    ApiResult<{ message: string }>,
    ApiError,
    CompleteableMutationInput
  >,
): UseMutationResult<
  ApiResult<{ message: string }>,
  ApiError,
  CompleteableMutationInput
> {
  return useMutation<
    ApiResult<{ message: string }>,
    ApiError,
    CompleteableMutationInput
  >(async function ({ id, type }) {
    return await MutationFetchFunction({
      method: 'PATCH',
      url: `${type}/${id}/complete`,
    });
  }, options);
}

export function useRevertComplete(
  options?: UseMutationOptions<
    ApiResult<{ message: string }>,
    ApiError,
    CompleteableMutationInput
  >,
): UseMutationResult<
  ApiResult<{ message: string }>,
  ApiError,
  CompleteableMutationInput
> {
  return useMutation<
    ApiResult<{ message: string }>,
    ApiError,
    CompleteableMutationInput
  >(async function ({ id, type }) {
    return await MutationFetchFunction({
      method: 'PATCH',
      url: `${type}/${id}/revert-complete`,
    });
  }, options);
}

export function useVoid(
  options?: UseMutationOptions<
    ApiResult<{ message: string }>,
    ApiError,
    VoidableMutationInput
  >,
): UseMutationResult<
  ApiResult<{ message: string }>,
  ApiError,
  VoidableMutationInput
> {
  return useMutation<
    ApiResult<{ message: string }>,
    ApiError,
    VoidableMutationInput
  >(async function ({ id, type }) {
    return await MutationFetchFunction({
      method: 'PATCH',
      url: `${type}/${id}/void`,
    });
  }, options);
}

export function useResolve(
  options?: UseMutationOptions<
    ApiResult<{ message: string }>,
    ApiError,
    ResolveableMutationInput
  >,
): UseMutationResult<
  ApiResult<{ message: string }>,
  ApiError,
  ResolveableMutationInput
> {
  return useMutation<
    ApiResult<{ message: string }>,
    ApiError,
    ResolveableMutationInput
  >(async function ({ id, type }) {
    return await MutationFetchFunction({
      method: 'PATCH',
      url: `${type}/${id}/resolve`,
    });
  }, options);
}

export function useRevertDraft(
  options?: UseMutationOptions<
    ApiResult<{ message: string }>,
    ApiError,
    RevertDraftMutationInput
  >,
): UseMutationResult<
  ApiResult<{ message: string }>,
  ApiError,
  RevertDraftMutationInput
> {
  return useMutation<
    ApiResult<{ message: string }>,
    ApiError,
    RevertDraftMutationInput
  >(async function ({ id, type }) {
    return await MutationFetchFunction({
      method: 'PATCH',
      url: `${type}/${id}/revert-draft`,
    });
  }, options);
}

export function useRevertVoid(
  options?: UseMutationOptions<
    ApiResult<{ message: string }>,
    ApiError,
    RevertVoidMutationInput
  >,
): UseMutationResult<
  ApiResult<{ message: string }>,
  ApiError,
  RevertVoidMutationInput
> {
  return useMutation<
    ApiResult<{ message: string }>,
    ApiError,
    RevertVoidMutationInput
  >(async function ({ id, type }) {
    return await MutationFetchFunction({
      method: 'PATCH',
      url: `${type}/${id}/revert-void`,
    });
  }, options);
}

export function usePrintInvoice(
  options?: UseMutationOptions<any, ApiError, PrintInvoiceMutationInput>,
): UseMutationResult<any, ApiError, PrintInvoiceMutationInput> {
  return useMutation<any, ApiError, PrintInvoiceMutationInput>(async function ({
    type,
    id,
    body,
  }) {
    return await MutationFetchFunction({
      url: `${type}/${id}/print-pdf`,
      method: 'POST',
      body,
    });
  },
  options);
}

export function usePrintVoucher(
  options?: UseMutationOptions<any, ApiError, PrintVoucherMutationInput>,
): UseMutationResult<any, ApiError, PrintVoucherMutationInput> {
  return useMutation<any, ApiError, PrintVoucherMutationInput>(async function ({
    type,
    id,
  }) {
    return await MutationFetchFunction({
      url: `${type}/${id}/print-voucher`,
      method: 'POST',
    });
  },
  options);
}
